import { RefObject, useEffect, useRef } from "react";

interface UseAutoScrollChatOptions {
  /**
   * When this value changes, scroll-intent event listeners are re-attached.
   * Use a key that changes when the container element itself may be remounted
   * (e.g., a panel collapse/expand toggle).
   */
  attachKey?: unknown;
  /**
   * When this value changes, the internal "previous message count" tracker is
   * reset to 0. Use a key that changes when the chat context resets — typically
   * the projectId — so the next message in a fresh chat is treated as new.
   */
  resetKey?: unknown;
}

/**
 * Manages chat auto-scroll behavior.
 *
 * - Auto-scrolls to bottom when new messages are appended.
 * - Continues following streaming updates while engaged.
 * - Disengages when the user scrolls up (wheel / touch / keyboard / scrollbar).
 * - Re-engages once the user scrolls back near the bottom (hysteresis: 50px to
 *   disengage, 30px to re-engage) to prevent flickering.
 *
 * `messages` is taken as a reference rather than just a count because the
 * caller's conversation slice produces a new array reference on every
 * streaming chunk update. Comparing the array reference (effect dep) is
 * what catches those streaming updates; the message *count* is then used
 * separately to detect newly-appended messages.
 */
export function useAutoScrollChat(
  containerRef: RefObject<HTMLElement | null>,
  messages: { length: number } | readonly unknown[],
  { attachKey, resetKey }: UseAutoScrollChatOptions = {},
): void {
  const shouldAutoScrollRef = useRef<boolean>(true);
  const userIsScrollingRef = useRef<boolean>(false);
  const userScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const prevMessageCountRef = useRef<number>(0);

  // Effect: User scroll-intent detection.
  // Uses wheel/touch/keyboard events (never fired by programmatic scrolls)
  // to disengage auto-scroll on upward user input and re-engage at bottom.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const disengageAutoScroll = () => {
      shouldAutoScrollRef.current = false;
      userIsScrollingRef.current = true;
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
      // Clear active flag after 150ms of inactivity (covers trackpad momentum)
      userScrollTimeoutRef.current = setTimeout(() => {
        userIsScrollingRef.current = false;
      }, 150);
    };

    // Only disengage on scroll UP; scroll down shouldn't break auto-follow
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) disengageAutoScroll();
    };

    // Touch: track direction, only disengage when scrolling content UP (finger moves down)
    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches[0].clientY > touchStartY) {
        disengageAutoScroll();
      }
    };

    // Disengage on upward nav keys only
    const handleKeyDown = (e: KeyboardEvent) => {
      if (["ArrowUp", "PageUp", "Home"].includes(e.key)) {
        disengageAutoScroll();
      }
    };

    // Position-based fallback: handles scrollbar drag and any other input method.
    // Uses hysteresis (50px to disengage, 30px to re-engage) to prevent flickering.
    const handleScroll = () => {
      if (userIsScrollingRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distFromBottom = scrollHeight - scrollTop - clientHeight;
      if (shouldAutoScrollRef.current && distFromBottom > 50) {
        shouldAutoScrollRef.current = false;
      } else if (!shouldAutoScrollRef.current && distFromBottom <= 30) {
        shouldAutoScrollRef.current = true;
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: true });
    container.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: true,
    });
    container.addEventListener("keydown", handleKeyDown, { passive: true });
    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("keydown", handleKeyDown);
      container.removeEventListener("scroll", handleScroll);
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachKey]);

  // Effect: reset prevMessageCount when the chat context changes (e.g.,
  // navigating between projects). Mirrors the original IDEChatPanel behavior
  // of zeroing the counter inside the project-boundary reset.
  useEffect(() => {
    prevMessageCountRef.current = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Effect: Auto-scroll on new messages and streaming updates.
  // Triggered by the array reference itself so streaming chunk updates
  // (which keep length the same but produce a new array) still scroll.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const currentCount = messages.length;
    const isNewMessage = currentCount > prevMessageCountRef.current;
    prevMessageCountRef.current = currentCount;

    if (isNewMessage) {
      // New message added — always scroll to bottom and re-engage auto-scroll
      shouldAutoScrollRef.current = true;
      userIsScrollingRef.current = false;
      container.scrollTop = container.scrollHeight;
    } else if (shouldAutoScrollRef.current && !userIsScrollingRef.current) {
      // Streaming update — auto-scroll engaged and user isn't actively scrolling
      container.scrollTop = container.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);
}
